import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {
    border,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";

import Field from "./field.tsx";
import {CorrectIcon, IncorrectIcon} from "./icons.tsx";
import type {AnswerKey} from "./types.ts";

interface Props {
    letter: AnswerKey;
    isCorrect: boolean;
    isDuplicate?: boolean;
    setAnswerKey: (letter: AnswerKey) => void;
    setText: (text: string) => void;
    text: string;
}

const Answer: React.FC<Props> = (props) => {
    const {
        letter,
        isCorrect,
        isDuplicate = false,
        setAnswerKey,
        setText,
        text,
    } = props;
    const handleAnswerKeyClick = (): void => {
        // Clicking on the correct answer again does nothing.
        if (!isCorrect) {
            setAnswerKey(letter);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<Element>): void => {
        event.preventDefault();
        if (event.key === " " || event.key === "Enter") {
            if (!isCorrect) {
                setAnswerKey(letter);
            }
        }
    };

    return (
        <View style={styles.answer}>
            <View
                onClick={handleAnswerKeyClick}
                onKeyDown={handleKeyDown}
                style={styles.answerIcon}
                role="radio"
                aria-checked={isCorrect}
                aria-label={t`Answer ${letter}`}
            >
                {isCorrect ? (
                    <CorrectIcon />
                ) : (
                    <IncorrectIcon letter={letter} />
                )}
            </View>
            <View style={styles.textContainer}>
                <Field
                    text={text}
                    setText={setText}
                    isDuplicate={isDuplicate}
                    ariaLabel={t`Answer ${letter}`}
                />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    answer: {
        borderBottomStyle: "solid",
        borderBottomColor: semanticColor.core.border.neutral.subtle,
        borderBottomWidth: border.width.thin,
        ":last-child": {
            borderBottom: "unset",
        },
        flexDirection: "row",
        marginInlineStart: spacing.xxxSmall_4,
        marginInlineEnd: spacing.xxxSmall_4,
        justifyContent: "space-between",
        flexBasis: 80,
        flexGrow: 1,
        display: "flex",
        cursor: "pointer",
    },
    answerIcon: {
        paddingInlineEnd: spacing.xSmall_8,
        marginInlineEnd: spacing.xSmall_8,
        marginBlockStart: spacing.xSmall_8,
    },
    textContainer: {
        marginBlockStart: spacing.xxSmall_6,
        flexGrow: 1,
    },
});

export default Answer;
